import { Text, withDatasourceCheck, NextImage } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import useHelpers from 'hooks/helpers';
import AbrdnSelect from 'components/Generic/Forms/FormFields/AbrdnSelect';
import AbrdnButton from 'components/Generic/Buttons/AbrdnButton';
import Rating from 'components/includes/Rating';
import { useMemo, useState } from 'react';
import styles from './styles/CallOutSection.module.scss';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

type DropdownOptions = {
  label: string;
  linktype: string;
  name: string;
  selected: boolean;
  target: string;
  url: string;
};

type Props = ComponentProps & {
  fields: {
    ctaLabel: {
      value: string;
    };
    description: {
      value: string;
    };
    dropdownPlaceholder: {
      value: string;
    };
    employeeName: {
      value: string;
    };
    icon: {
      value: {
        alt: string;
        ['data-type']: string;
        src: string;
      };
    };
    jobTitle: {
      value: string;
    };
    stars: {
      value: string;
    };
    starsDescription: {
      value: string;
    };
    title: {
      value: string;
    };
    items: {
      displayName: string;
      id: string;
      name: string;
      template: string;
      fields: {
        dropdownOption: {
          value: {
            anchor: string;
            href: string;
            linktype: string;
            target: string;
            text: string;
            url: string;
          };
        };
      };
    }[];
  };
};

const CallOutSection = ({ rendering, fields }: Props): JSX.Element => {
  const { anchorId, backgroundClass } = useHelpers(rendering);
  const { sitecoreContext } = useSitecoreContext();
  const isExperienceEditor = sitecoreContext.pageEditing;

  const [selectedOption, setSelectedOption] = useState<any>(undefined);

  const isImageExist = fields.icon.value?.src;
  const isPictogram = fields.icon.value?.['data-type'] === 'image/svg+xml';

  const dropdownOptions: Array<DropdownOptions> = useMemo(() => {
    if (!fields.items.length) {
      return [];
    }
    return fields.items.map((item) => {
      const { text, href, linktype, url, target } = item.fields.dropdownOption.value;
      return {
        name: text,
        label: text,
        url: url || href,
        target,
        linktype,
        selected: text === selectedOption?.label,
      };
    });
  }, [fields.items, selectedOption]);

  const dropdownProps = useMemo(() => {
    return {
      label: {
        value: fields?.description.value,
      },
      placeholder: {
        value: fields.dropdownPlaceholder.value,
      },
      options: dropdownOptions,
    };
  }, [dropdownOptions, fields?.description.value, fields.dropdownPlaceholder.value]);

  const ctaData = useMemo(() => {
    return {
      value: {
        text: fields.ctaLabel?.value,
        href: selectedOption?.url || '',
        target: selectedOption?.target || '',
        backgroundToChange: false,
        linktype: selectedOption?.linktype,
      },
    };
  }, [selectedOption, fields.ctaLabel?.value]);

  const starsDescription = {
    ...fields.starsDescription,
    value: fields.starsDescription.value.replace('{token.stars}', fields.stars.value),
  };

  const updateValue = ({ label }) => {
    setSelectedOption(dropdownOptions.find((option) => option.label === label));
  };

  return (
    <section
      id={anchorId}
      data-e2e-component-uid={rendering.uid}
      className="short-section default-section-margin"
    >
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div
              className={`call-out-with-options py-xl-6 py-3 ps-xl-8 d-flex align-items-start ${backgroundClass}`}
            >
              {(isImageExist || isExperienceEditor) && (
                <div
                  className={`${styles['call-out-with-options__image']} me-6 flex-shrink-0 d-none d-xl-block`}
                >
                  <NextImage field={fields.icon} width={200} height={160} />
                  {(!isPictogram || isExperienceEditor) && (
                    <>
                      <Text
                        tag="p"
                        field={fields.employeeName}
                        class="background-to-change footnote footnote--bold mt-2"
                      />
                      <Text
                        tag="p"
                        field={fields.jobTitle}
                        class="background-to-change footnote mt-1"
                      />
                    </>
                  )}
                </div>
              )}

              <div
                className={`${styles['call-out-with-options__content']} px-xl-3 px-2 flex-grow-1`}
              >
                <Text tag="h3" field={fields.title} class="background-to-change" />
                <div className="call-out-with-options__content__dropdown d-flex align-items-xl-end y flex-xl-row flex-column align-items-start mt-4 mt-md-4">
                  <AbrdnSelect
                    options={dropdownProps.options}
                    title={dropdownProps.label.value}
                    placeholder={dropdownProps.placeholder.value}
                    className="flex-grow-1 w-100"
                    onChange={updateValue}
                  />
                  <AbrdnButton
                    buttonType="primary"
                    className={`ms-xl-4 mt-3 mt-xl-0 flex-shrink-0 background-to-change ${
                      !selectedOption && !isExperienceEditor ? 'disabled' : ''
                    }`}
                    linkField={!isExperienceEditor ? ctaData : false}
                    showExternalIcon={false}
                  >
                    <>
                      <Text tag="p" field={fields.ctaLabel} />
                      <i
                        className={`bi bi-arrow-right ${styles['button-arrow']} ms-1`}
                        aria-hidden="true"
                      />
                    </>
                  </AbrdnButton>
                </div>

                <div
                  className={`call-out-with-options__rating mt-xl-4 background-to-change ${
                    Number(fields.stars.value) || starsDescription.value ? 'mt-3' : ''
                  }`}
                >
                  {Number(fields.stars.value) !== 0 && (
                    <Rating rating={Number(fields.stars.value)} />
                  )}
                  <Text tag="p" field={starsDescription} className="mt-2 mt-xl-1 body-small" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default withDatasourceCheck()(CallOutSection);
